// CORE
import { useState, useEffect } from "react";
import { Box, Divider, Button } from "@mui/material";
import { styled } from "@mui/system";
import ContainedButton from "src/components/common/ContainedButton";
import { useHistory } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import dayjs from "dayjs";

// REDUX
import { useUserSlice } from "src/store/features/user.slice";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import { useDispatch } from "react-redux";
import {
  setFetchRecentTrades,
  resetTradeDetails,
  resetTradeRequest,
  useSwapSlice,
} from "src/store/features/swap.slice";
import api from "src/store/features/api";
import { useTargetStateV2 } from "src/store/features/TargetOrdersV2.slice";
import {
  CreateTargetOrderCtApiArg,
  CreateTradeDto,
  DcaStrategySummaryRes,
  HoldingRes,
} from "src/store/features/api/generated";
import { useDcaSlice } from "src/store/features/dca.slice";

// HOOKS
import useTradeInputs from "../swap/hooks/useTradeInputs";
import useTransactionDisclosure from "src/utils/hooks/useTransactionDisclosure";

// ICONS
import ClearIcon from "@mui/icons-material/Clear";
import RemoveIcon from "@mui/icons-material/Remove";
import HelpIcon from "@mui/icons-material/Help";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// CONSTANTS
import { HELP_TOOLTIP, RESERVED_BALANCE_TOOLTIP, TRADE_CONFIRMATION_MODAL_MAP } from "../constants";

// TYPES
import { TradeType } from "../types";

// UTILS
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { formatPrice } from "src/components/portfolio/helpers";

export const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  boxShadow: "0px 4px 20px 0px #ADC0CB4D",
  width: "100%",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px",
  gap: "24px",
  borderRadius: "28px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "100%",
    padding: "16px",
  },
}));

export const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  paddingBottom: "12px",
}));

export const Header = styled("div")(() => ({
  display: "flex",
  gap: "12px",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
}));

interface TextProps {
  color?: string;
  weight?: number;
}
export const Text = styled("div")<TextProps>(({ color, weight }) => ({
  color: color ?? "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: weight ?? 400,
  lineHeight: "20px",
}));

export const ButtonsWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  width: "100%",
}));

interface CalcTextProps {
  weight?: number;
}
export const CalcText = styled("div")<CalcTextProps>(({ weight }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: weight ?? 500,
  lineHeight: "16px",
}));

export const CollapseContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px",
  borderRadius: "10px",
  width: "100%",
  border: "0.5px solid #E5E7E8",
}));

export const BrokerProfileCircle = styled("div")(() => ({
  width: "35px",
  height: "35px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "100px",
  background: "#FFF",
}));

interface TradeConfirmationModalProps {
  tradeType: TradeType;
  open: boolean;
  sellUnits: string;
  sellCode: string;
  buyUnits: string;
  buyCode: string;
  fee: number;
  priceData: string;
  remainingUnits?: string | number;
  onClose: () => void;
  frequency?: string;
}

/**
 * TradeConfirmationModal component
 *
 * This component is used to display a modal for trade order confirmation.
 * If the user does not have two-factor authentication (2FA) enabled, it warns the user
 * and provides an option to enable 2FA before proceeding with the trade order.
 */
const TradeConfirmationModal = ({
  tradeType,
  open,
  sellUnits,
  sellCode,
  buyUnits,
  buyCode,
  fee,
  priceData,
  remainingUnits,
  onClose,
  frequency,
}: TradeConfirmationModalProps) => {
  const [tradeErr, setTradeErr] = useState<string | null>(null);
  const [createTrade, { isLoading: isCreateTradeLoading, isSuccess: isCreateTradeSuccess }] =
    api.useCreateCtMutation();
  const [userData] = api.useLazyGetUserDatav2Query();

  const [createTargetOrderCt, { isLoading: isTargetLoading, isSuccess: isCreateTargetSuccess }] =
    api.useCreateTargetOrderCtMutation();

  const [postStrategy, { isLoading: isScheduleOrderLoading, isSuccess: isCreateScheduleSuccess }] =
    api.useCreateDcaStrategyMutation();

  const isLoadingState = isCreateTradeLoading || isTargetLoading || isScheduleOrderLoading;
  const isSuccessState = isCreateTradeSuccess || isCreateTargetSuccess || isCreateScheduleSuccess;

  const { tradeRequest } = useSwapSlice();

  const { orderType } = useTargetStateV2();

  // user input data - hook
  const { resetInputs } = useTradeInputs();

  // Disclosure timestamps (MN/CT) to send with trade create payload (from modal or already-accepted-today)
  const { disclosurePayload } = useTransactionDisclosure(undefined, "trade");

  const dispatch = useDispatch();

  const {
    is2FAEnabled,
    broker: { imgUrl },
    userHoldings,
  } = useUserSlice();

  const matchedAsset = (): HoldingRes | undefined => {
    if (tradeType !== "target") return;

    const assetHolding = userHoldings.find((holding) =>
      orderType === "Sell"
        ? holding.currencyConfig.code === sellCode
        : holding.currencyConfig.code === buyCode,
    );

    return assetHolding;
  };

  const history = useHistory();

  /**
   * Handles creating CT from order. This function:
   * 1. Toggles the trade confirmation modal closing it.
   * 2. Creates a DTO for the trade based on current input values.
   * 3. Removes properties with units equal to 0 to determine which side of the trade is known.
   * 4. Calls the API to create the trade.
   * 5. Resets the form to its default state and toggles the trade success modal if the trade creation is successful.
   * 6. Handles any errors that occur during the trade creation process.
   */
  const handleOnSwapConfirm = (): void => {
    setTradeErr(null);
    if (!tradeRequest) return;

    const timezone =
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
    const createTradeDto: CreateTradeDto = {
      ...tradeRequest,
      datetime: new Date().toLocaleString(),
      ...(disclosurePayload && {
        disclosureRequestDateTime: disclosurePayload.disclosureRequestDateTime,
        disclosureAcceptanceDateTime: disclosurePayload.disclosureAcceptanceDateTime,
        timezone: disclosurePayload.timezone,
      }),
    };

    trackEvent("Placing Order", { orderPayload: createTradeDto }, "Swap");
    // Call create trade API here, then reset and toggleTradeSuccess
    createTrade({ createTradeDto })
      .unwrap()
      .then((): void => {
        trackEvent("Order Placed", { orderPayload: createTradeDto }, "Swap");
        dispatch(setFetchRecentTrades());
        userData();
      })
      .catch((error): void => {
        trackEvent("Error Placing Order", { orderPayload: createTradeDto }, "Swap");
        setTradeErr("Something went wrong. Please try again, or contact your broker.");
      });
  };

  const handleTargetSubmit = () => {
    setTradeErr(null);

    if (orderType === "Buy") {
      trackEvent(`Target_Order_Submitted`, {
        Submit_Target_Buy: sellCode,
      });
      const timezone =
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
      const buyReqBody: CreateTargetOrderCtApiArg = {
        createTargetOrderDto: {
          buy: {
            targetPrice: Number(priceData),
            units: Number(sellUnits),
            code: sellCode,
          },
          sell: { code: buyCode },
          datetime: new Date().toLocaleString(),
          ...(disclosurePayload && {
            disclosureRequestDateTime: disclosurePayload.disclosureRequestDateTime,
            disclosureAcceptanceDateTime: disclosurePayload.disclosureAcceptanceDateTime,
            timezone: disclosurePayload.timezone,
          }),
        },
      };
      createTargetOrderCt(buyReqBody)
        .unwrap()
        .catch((error): void => {
          setTradeErr("Something went wrong. Please try again, or contact your broker.");
        });
    } else {
      trackEvent(`Target_Order_Submitted`, {
        Submit_Target_Sell: buyCode,
      });
      const timezone =
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
      const sellReqBody: CreateTargetOrderCtApiArg = {
        createTargetOrderDto: {
          buy: { code: buyCode },
          sell: {
            targetPrice: Number(priceData),
            units: Number(sellUnits),
            code: sellCode,
          },
          datetime: new Date().toLocaleString(),
          ...(disclosurePayload && {
            disclosureRequestDateTime: disclosurePayload.disclosureRequestDateTime,
            disclosureAcceptanceDateTime: disclosurePayload.disclosureAcceptanceDateTime,
            timezone: disclosurePayload.timezone,
          }),
        },
      };
      createTargetOrderCt(sellReqBody)
        .unwrap()
        .catch((error): void => {
          setTradeErr("Something went wrong. Please try again, or contact your broker.");
        });
    }
  };

  const { summary } = useDcaSlice();

  const createStrategy = async () => {
    setTradeErr(null);
    const dcaStrategySummaryRes = {
      ...(summary as DcaStrategySummaryRes),
      ...(disclosurePayload && {
        disclosureRequestDateTime: disclosurePayload.disclosureRequestDateTime,
        disclosureAcceptanceDateTime: disclosurePayload.disclosureAcceptanceDateTime,
        timezone: disclosurePayload.timezone,
      }),
    };
    await postStrategy({ dcaStrategySummaryRes })
      .unwrap()
      .then(() => {
        trackEvent("User Placed Scheduled order", { ...dcaStrategySummaryRes }, "Scheduled_Orders");
      })
      .catch(() => {
        setTradeErr("Something went wrong. Please try again, or contact your broker.");
      });
  };

  const handleClose = () => {
    dispatch(resetTradeDetails());
    dispatch(resetTradeRequest());
    resetInputs();
    onClose();
  };

  const configSubtitleHeader = () => {
    if (tradeErr) {
      return tradeErr;
    }
    if (tradeType === "swap") {
      return isCreateTradeSuccess
        ? "Your order has been submitted. If you have any issues with your order, please feel free to contact me."
        : "Please confirm your order to proceed";
    } else if (tradeType === "target") {
      return isCreateTargetSuccess
        ? "Your target has been submitted. If you have any issues with your order, please feel free to contact me."
        : "Please confirm your target order to proceed";
    } else if (tradeType === "scheduled") {
      return isCreateScheduleSuccess
        ? "Thanks for setting up your scheduled order! We'll execute orders at your requested intervals. If you have any issues with your order, please feel free to contact me."
        : "Please confirm your scheduled order to proceed";
    }
  };

  /**
   * Swap Config
   */
  const swapConfig = {
    action: "Swap",
    priceText: "Market Price",
    priceData: `${priceData}`,
    header: isCreateTradeSuccess ? "Order Submitted" : "Order Summary",
    expectedOutputHeader: "Expected Output",
    expectedOutput: `Approx. ${Number(buyUnits).toFixed(5)} ${buyCode}`,
    subtitle: configSubtitleHeader(),
    button1: "Edit Order",
    button2: isCreateTradeSuccess ? "Add Another Trade" : "Confirm",
    button1Func: onClose,
    button2Func: isCreateTradeSuccess ? handleClose : handleOnSwapConfirm,
  };

  /**
   * Target Config
   */
  const targetsConfig = {
    action: `You ${orderType}`,
    priceText: "Target Price",
    priceData: `${priceData} ${buyCode}`,
    header: isCreateTargetSuccess ? "Target Submitted" : "Target Summary",
    expectedOutputHeader: "Expected Output",
    expectedOutput: `Approx. ${
      orderType === "Sell" ? `${buyUnits} ${buyCode}` : sellUnits + " " + sellCode
    }`,
    subtitle: configSubtitleHeader(),
    button1: "Edit Target",
    button2: isCreateTargetSuccess ? "Add Another Target" : "Confirm Target",
    button1Func: onClose,
    button2Func: isCreateTargetSuccess ? handleClose : handleTargetSubmit,
  };

  /**
   * Scheduled Config
   */
  const formattedOutput = () => {
    if (summary?.buy?.value && summary?.totalTriggers) {
      return formatCurrency(summary?.buy?.value * summary?.totalTriggers, buyCode);
    }
  };

  const scheduledConfig = {
    action: "You buy",
    priceText: `${frequency} Starting`,
    priceData: `${priceData}`,
    header: isCreateScheduleSuccess ? "Order Scheduled" : "Order Summary",
    expectedOutputHeader: "You receive",
    expectedOutput: formattedOutput(),
    subtitle: configSubtitleHeader(),
    button1: "Edit Order",
    button2: isCreateScheduleSuccess ? "Schedule Another Order" : "Confirm Order",
    button1Func: onClose,
    button2Func: isCreateScheduleSuccess ? handleClose : createStrategy,
  };

  const tradeConfigs: Record<TradeType, Record<string, any>> = {
    swap: swapConfig,
    target: targetsConfig,
    scheduled: scheduledConfig,
  };

  const selectedConfig = tradeConfigs[tradeType];

  useEffect(() => {
    if (open) {
      if (!is2FAEnabled) {
        trackEvent(
          "User Visited Trade Confirmation Modal",
          {
            modal: "Place Order 2FA Warning",
          },
          TRADE_CONFIRMATION_MODAL_MAP[tradeType],
        );
      } else {
        trackEvent(
          "User Visited Trade Confirmation Modal",
          {},
          TRADE_CONFIRMATION_MODAL_MAP[tradeType],
        );
      }
    }
  }, [open]);

  if (!is2FAEnabled && open) {
    return (
      <Wrapper>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
          gap="12px"
          sx={{
            background: tradeErr ? "#FEF5F6" : "#FEFCF8",
            border: `1px solid ${tradeErr ? "#F04D4D" : "#FCF6EB"}`,
          }}
          padding="8px 4px"
          borderRadius="10px">
          <BrokerProfileCircle>
            <img src={imgUrl} alt="profile" height="33px" />
          </BrokerProfileCircle>
          <CalcText weight={400}>
            It looks like you don't have two factor authentication enabled.
          </CalcText>
        </Box>
        <Text weight={400}>Click the button below to enable two factor authentication.</Text>
        <ButtonsWrapper>
          <ContainedButton
            color="neutral"
            borderRadius="28px"
            sx={{
              padding: "12px 16px",
              background: "#FFFFFF",
              border: "1px solid #E5E9EB",
            }}
            height="46px"
            onClick={onClose}>
            <Text>Go Back</Text>
          </ContainedButton>
          <ContainedButton
            id="2fa-enable-button"
            color="salmon"
            borderRadius="28px"
            sx={{ padding: "12px 16px" }}
            height="46px"
            onClick={() => history.push("settings/security")}>
            <Text color="#FFF" weight={600}>
              Enable 2FA
            </Text>
          </ContainedButton>
        </ButtonsWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Button
          onClick={onClose}
          style={{ cursor: "pointer", minWidth: "0", padding: "0" }}
          disabled={isLoadingState}>
          <ArrowBackIosNewIcon sx={{ width: "16px" }} />
        </Button>
        <Box display="flex" alignItems="center" gap="8px">
          {selectedConfig.header} {isLoadingState && <LoadingSpinner size={16} color="#0BEC9F" />}{" "}
          {isSuccessState && (
            <CheckCircleIcon sx={{ color: "#0BEC9F", width: "16px", height: "16px" }} />
          )}
        </Box>
        <div />
      </Header>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        width="100%"
        gap="12px"
        sx={{
          background: tradeErr ? "#FEF5F6" : "#FEFCF8",
          border: `1px solid ${tradeErr ? "#F04D4D" : "#FCF6EB"}`,
        }}
        padding="8px 4px"
        borderRadius="10px">
        <BrokerProfileCircle>
          <img src={imgUrl} alt="profile" height="33px" />
        </BrokerProfileCircle>
        <CalcText weight={400}>{selectedConfig.subtitle}</CalcText>
      </Box>
      <CollapseContent>
        <Box display="flex" justifyContent="space-between" width="100%">
          <CalcText>{selectedConfig.action}</CalcText>
          <CalcText>
            {sellUnits} {sellCode}
          </CalcText>
        </Box>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap="12px">
            <Box
              width="16px"
              height="16px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius="25px"
              bgcolor="#E5E9EB">
              <ClearIcon sx={{ width: "8px", color: "#002035" }} />
            </Box>
            <CalcText weight={400}>{selectedConfig.priceText}</CalcText>
          </Box>
          <CalcText weight={400}>{selectedConfig.priceData}</CalcText>
        </Box>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap="12px">
            <Box
              width="16px"
              height="16px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius="25px"
              bgcolor="#E5E9EB">
              <RemoveIcon sx={{ width: "8px", color: "#002035" }} />
            </Box>
            <CalcText weight={400}>Broker Fee</CalcText>
            <BalanceTooltip message={HELP_TOOLTIP} contrast>
              <HelpIcon sx={{ width: "12px", height: "12px", color: "#002035" }} />
            </BalanceTooltip>
          </Box>
          <CalcText weight={400}>{fee}%</CalcText>
        </Box>
        {selectedConfig.expectedOutput && (
          <>
            <Divider
              sx={{
                background: "#E5E7E8",
                width: "100%",
                borderBottomWidth: "0",
                height: "1px",
              }}
            />
            <Box display="flex" justifyContent="space-between" width="100%">
              <CalcText>{selectedConfig.expectedOutputHeader}</CalcText>
              <CalcText>{selectedConfig.expectedOutput}</CalcText>
            </Box>
          </>
        )}
      </CollapseContent>

      {tradeType === "target" && (
        <CollapseContent>
          <Box display="flex" justifyContent="space-between" width="100%">
            <CalcText>Total {orderType === "Sell" ? sellCode : buyCode} Balance</CalcText>
            <CalcText>{matchedAsset()?.available}</CalcText>
          </Box>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center" gap="12px">
              <Box
                width="16px"
                height="16px"
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderRadius="25px"
                bgcolor="#E5E9EB">
                <RemoveIcon sx={{ width: "8px", color: "#002035" }} />
              </Box>
              <CalcText weight={400}>Reserved Balance</CalcText>
              <BalanceTooltip message={RESERVED_BALANCE_TOOLTIP} contrast>
                <HelpIcon sx={{ width: "12px", height: "12px", color: "#002035" }} />
              </BalanceTooltip>
            </Box>
            <CalcText weight={400}>
              {orderType === "Sell" ? sellUnits : buyUnits}{" "}
              {orderType === "Sell" ? sellCode : buyCode}
            </CalcText>
          </Box>
          <Divider
            sx={{
              background: "#E5E7E8",
              width: "100%",
              borderBottomWidth: "0",
              height: "1px",
            }}
          />
          <Box display="flex" justifyContent="space-between" width="100%">
            <CalcText>Available Balance</CalcText>
            <CalcText>
              {remainingUnits} {orderType === "Sell" ? sellCode : buyCode}
            </CalcText>
          </Box>
        </CollapseContent>
      )}
      {tradeType === "scheduled" && summary && (summary.endDate || summary.spendLimit) && (
        <CollapseContent>
          {summary.spendLimit && (
            <Box display="flex" justifyContent="space-between" width="100%">
              <CalcText weight={400}>Spend Limit</CalcText>
              <CalcText weight={400}>{formatPrice(summary.spendLimit, buyCode)}</CalcText>
            </Box>
          )}
          {summary.endDate && (
            <Box display="flex" justifyContent="space-between" width="100%">
              <CalcText weight={400}>End Date</CalcText>
              <CalcText weight={400}>{dayjs(summary.endDate).format("DD MMM YYYY")}</CalcText>
            </Box>
          )}
          <Divider
            sx={{
              background: "#E5E7E8",
              width: "100%",
              borderBottomWidth: "0",
              height: "1px",
            }}
          />
          <Box display="flex" justifyContent="space-between" width="100%">
            <CalcText>Number of orders</CalcText>
            <CalcText>{summary.totalTriggers}</CalcText>
          </Box>
        </CollapseContent>
      )}
      <ButtonsWrapper>
        {!isSuccessState && (
          <ContainedButton
            color="neutral"
            borderRadius="28px"
            sx={{
              padding: "12px 16px",
              background: "#FFFFFF",
              border: "1px solid #E5E9EB",
            }}
            height="46px"
            onClick={selectedConfig.button1Func}
            disabled={isLoadingState}>
            <Text>{selectedConfig.button1}</Text>
          </ContainedButton>
        )}
        <ContainedButton
          id={is2FAEnabled ? "confirm-order-button" : "2fa-enable-button"}
          color="salmon"
          borderRadius="28px"
          sx={{ padding: "12px 16px" }}
          height="46px"
          onClick={selectedConfig.button2Func}
          disabled={isLoadingState}>
          <Text color="#FFF" weight={600}>
            {selectedConfig.button2}
          </Text>
        </ContainedButton>
      </ButtonsWrapper>
    </Wrapper>
  );
};

export default TradeConfirmationModal;
